import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Button,
  ColumnLayout,
  Container,
  Flashbar,
  Form,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react-v3';

import { useLocation } from 'react-router-dom';
import * as validate from '../../commons/validationUtils';
import { Page } from '../../routes/Paths';
import { editTemplate } from 'src/api/permissions';
import { FORM_CONTENT_TYPE } from 'src/commons/constants';
import {
  getAccessManagementComponent,
  getAccessRequestWorkflowComponent,
  getDataAgreementOptionsForOwner,
  isAccessManagementType,
  isAccessRequestWorkflowType,
  populateAccessManagementInfo,
  populateAccessRequestWorkflows,
} from 'src/components/templates/common';
import { isD3AServiceAvailable } from 'src/api/config';
import { AccessRequestWorkflow } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { getDataAgreement } from 'src/api/d3a';
import { CommonPageProps } from 'src/components/common/CommonPageProps';

export interface EditTemplateProps extends CommonPageProps {}

export const EditTemplate = (props: EditTemplateProps) => {
  const { state } = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [templateName, setTemplateName] = useState(null);
  const [description, setDescription] = useState(null);
  const [simTemplateName, setSimTemplateName] = useState(null);
  const [simTemplateLink, setSimTemplateLink] = useState(null);
  // common fields
  const [approvalTemplateLink, setApprovalTemplateLink] = useState(null);
  const [approvalTemplateTitle, setApprovalTemplateTitle] = useState(null);
  const [autoApproval, setAutoApproval] = useState(false);
  const [allowMergeApprovalWorkflow, setAllowMergeApprovalWorkflow] = useState(false);
  // access request workflow fields
  const [priorityOrder, setPriorityOrder] = useState<number>(undefined);
  const [arwRuleName, setArwRuleName] = useState(null);
  const [arwRuleDesc, setArwRuleDesc] = useState(null);
  const [arwRule, setArwRule] = useState(null);
  const [arwDataAgreement, setArwDataAgreement] = useState(null);
  const [arwDataAgreementModalVisible, setArwDataAgreementModalVisible] = useState(false);
  const [arwDataAgreementId, setArwDataAgreementId] = useState(null);
  const [arwDataAgreementName, setArwDataAgreementName] = useState(null);
  const [arwDataAgreementOptions, setArwDataAgreementOptions] = useState([]);
  const [arwDataAgreementOwnerId, setArwDataAgreementOwnerId] = useState(undefined);
  const [optimisticApprovalEnabled, setOptimisticApprovalEnabled] = useState(false);

  // Reset optimistic approval when data agreement becomes invalid
  useEffect(() => {
    if ((!arwDataAgreementId || arwDataAgreementId.length !== 36) && optimisticApprovalEnabled) {
      setOptimisticApprovalEnabled(false);
    }
  }, [arwDataAgreementId]);

  useEffect(() => {
    props.setContentType(FORM_CONTENT_TYPE);
    if (!state) {
      props.setRedirect(Page.TEMPLATES);
    } else if (state.template) {
      // set current variable from state template.
      setTemplateName(state.template.name);
      setDescription(state.template.description);
      if (isAccessManagementType(state.template)) {
        if (state.template.accessManagementInfo?.simTemplates) {
          setSimTemplateLink(state.template.accessManagementInfo?.simTemplates[0].simTemplateLink);
          setSimTemplateName(state.template.accessManagementInfo?.simTemplates[0].simTemplateName);
        }
        if (state.template.accessManagementInfo?.approvalsWorkflowTemplate) {
          setApprovalTemplateLink(state.template.accessManagementInfo?.approvalsWorkflowTemplate.approvalTemplateLink);
          setApprovalTemplateTitle(
            state.template.accessManagementInfo?.approvalsWorkflowTemplate.approvalTemplateTitle,
          );
          setAutoApproval(state.template.accessManagementInfo?.approvalsWorkflowTemplate.autoApproval);
          setAllowMergeApprovalWorkflow(
            state.template.accessManagementInfo?.approvalsWorkflowTemplate.allowMergeApprovalWorkflow,
          );
        }
      }

      if (isAccessRequestWorkflowType(state.template) && state.template.accessRequestWorkflows) {
        const arw: AccessRequestWorkflow = state.template.accessRequestWorkflows[0];
        setPriorityOrder(arw.priorityOrder);
        if (arw.conditionRule) {
          setArwRuleName(arw.conditionRule.name);
          setArwRuleDesc(arw.conditionRule.description);
          setArwRule(arw.conditionRule.rule);
        }
        if (arw.dataAgreement) {
          setArwDataAgreementName(arw.dataAgreement.agreementName);
          setArwDataAgreementId(arw.dataAgreement.agreementId);
          setArwDataAgreementOwnerId(arw.dataAgreement.agreementOwnerId);
          // fetch current agreement and set.
          if (isD3AServiceAvailable()) {
            getDataAgreement({ id: arw.dataAgreement.agreementId }).then(
              (apiResp) => {
                setArwDataAgreement(apiResp.dataAgreements[0]);
              },
              (err) => {
                console.error(`Failed to get data agreement with id: ${arw.dataAgreement.agreementId}`, err);
                setArwDataAgreement(null);
              },
            );
          }
        }
        if (arw.approvalsWorkflowTemplate) {
          setApprovalTemplateLink(arw.approvalsWorkflowTemplate.approvalTemplateLink);
          setApprovalTemplateTitle(arw.approvalsWorkflowTemplate.approvalTemplateTitle);
          setAutoApproval(arw.approvalsWorkflowTemplate.autoApproval);
          setAllowMergeApprovalWorkflow(arw.approvalsWorkflowTemplate.allowMergeApprovalWorkflow);
        }
        if (arw.optimisticApprovalEnabled !== undefined) {
          setOptimisticApprovalEnabled(arw.optimisticApprovalEnabled);
        }
      }
      if (isD3AServiceAvailable()) {
        // set agreement options
        const ownerIds: string[] = props.activeWorkspace
          ? [props.activeWorkspace.workspaceId, props.activeWorkspace.groupId]
          : [props.activeGroup];
        getDataAgreementOptionsForOwner(ownerIds).then((options) => setArwDataAgreementOptions(options));
      }
    }
  }, []);

  const shouldDisableForm = () => {
    const strDifferent = (oldStr: string, newStr: string): boolean => {
      // if, for example, the old value is undefined and the new value is empty string, this should not be treated like a change.
      if (validate.isBlank(oldStr) && validate.isBlank(newStr)) {
        return false;
      }
      return oldStr != newStr;
    };
    const boolDifferent = (oldBool: boolean, newBool: boolean): boolean => {
      // Special case: null/undefined → false is NOT a change (both display as unchecked)
      if (oldBool == null && newBool === false) {
        return false;
      }
      // All other differences count as changes
      return oldBool != newBool;
    };
    const nameChanged = state.template.name != templateName;
    const descriptionChanged = state.template.description != description;
    const ami = state.template.accessManagementInfo;
    const accessManagementInfoChanged =
      isAccessManagementType(state.template) &&
      (strDifferent(ami?.simTemplates?.[0]?.simTemplateName, simTemplateName) ||
        strDifferent(ami?.simTemplates?.[0]?.simTemplateLink, simTemplateLink) ||
        strDifferent(ami?.approvalsWorkflowTemplate?.approvalTemplateLink, approvalTemplateLink) ||
        strDifferent(ami?.approvalsWorkflowTemplate?.approvalTemplateTitle, approvalTemplateTitle) ||
        boolDifferent(ami?.approvalsWorkflowTemplate?.autoApproval, autoApproval) ||
        boolDifferent(ami?.approvalsWorkflowTemplate?.allowMergeApprovalWorkflow, allowMergeApprovalWorkflow));
    const arw = state.template.accessRequestWorkflows?.[0];
    const accessRequestWorkflowsChanged =
      isAccessRequestWorkflowType(state.template) &&
      (arw?.priorityOrder != priorityOrder ||
        strDifferent(arw?.conditionRule?.name, arwRuleName) ||
        strDifferent(arw?.conditionRule?.description, arwRuleDesc) ||
        strDifferent(arw?.dataAgreement?.agreementId, arwDataAgreementId) ||
        strDifferent(arw?.dataAgreement?.agreementName, arwDataAgreementName) ||
        strDifferent(arw?.approvalsWorkflowTemplate?.approvalTemplateLink, approvalTemplateLink) ||
        strDifferent(arw?.approvalsWorkflowTemplate?.approvalTemplateTitle, approvalTemplateTitle) ||
        boolDifferent(arw?.approvalsWorkflowTemplate?.autoApproval, autoApproval) ||
        boolDifferent(arw?.approvalsWorkflowTemplate?.allowMergeApprovalWorkflow, allowMergeApprovalWorkflow) ||
        boolDifferent(arw?.optimisticApprovalEnabled, optimisticApprovalEnabled));
    // If nothing has changed, disable the submit button.
    if (!nameChanged && !descriptionChanged && !accessManagementInfoChanged && !accessRequestWorkflowsChanged) {
      return true;
    }
    // Check for required fields
    let isApprovalFalsy = validate.isFalsy(approvalTemplateTitle) || validate.isFalsy(approvalTemplateLink);
    let isDataAgreementFalsy = validate.isFalsy(arwDataAgreementId);
    let isSimFalsy = validate.isFalsy(simTemplateName) || validate.isFalsy(simTemplateLink);
    return (
      validate.isFalsy(templateName) ||
      (isAccessManagementType(state.template) && isSimFalsy && isApprovalFalsy) ||
      (isAccessRequestWorkflowType(state.template) && isDataAgreementFalsy && isApprovalFalsy)
    );
  };

  const handleConfirm = async () => {
    setButtonLoading(true);
    let accessManagementInfo = undefined;
    let accessRequestWorkflows = undefined;
    if (isAccessManagementType(state?.template)) {
      accessManagementInfo = populateAccessManagementInfo(
        simTemplateName,
        simTemplateLink,
        approvalTemplateLink,
        approvalTemplateTitle,
        autoApproval,
        allowMergeApprovalWorkflow,
      );
    }
    if (isAccessRequestWorkflowType(state?.template)) {
      accessRequestWorkflows = populateAccessRequestWorkflows(
        arwRule,
        arwRuleName,
        arwRuleDesc,
        arwDataAgreementId,
        arwDataAgreementName,
        arwDataAgreementOwnerId,
        approvalTemplateLink,
        approvalTemplateTitle,
        autoApproval,
        allowMergeApprovalWorkflow,
        priorityOrder,
        optimisticApprovalEnabled,
      );
    }
    try {
      let editInput = {
        templateId: state.template?.templateId,
        resourceId: state?.template?.resourceId,
        name: templateName,
        description: description,
        accessManagementInfo: accessManagementInfo,
        accessRequestWorkflows: accessRequestWorkflows,
        templateTriggerRules: state?.template?.templateTriggerRules,
      };
      const editResponse = await editTemplate(editInput);
      const status = editResponse.message == 'SUCCESS' ? 'success' : 'error';
      setButtonLoading(false);
      props.setRedirect(Page.TEMPLATES, false, {
        status,
        message: 'Template edited successfully',
      });
    } catch (err) {
      setNotifications([
        {
          type: 'error',
          content: `Failed to Edit the template for template id: ${state.template?.templateId}`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  return (
    <div>
      <Flashbar items={notifications}></Flashbar>
      <Form
        header={
          <Header variant='h1' description='Please specify the details of the template.'>
            Edit Template
          </Header>
        }
        actions={
          <SpaceBetween direction='horizontal' size='s'>
            <Button variant='link' onClick={() => props.setRedirect(Page.TEMPLATES)}>
              Cancel
            </Button>
            <Button variant='primary' loading={buttonLoading} onClick={handleConfirm} disabled={shouldDisableForm()}>
              {'Edit template'}
            </Button>
          </SpaceBetween>
        }
      >
        <Container header={<Header variant='h2'>Template details</Header>}>
          <ColumnLayout>
            <SpaceBetween size='m'>
              <FormField label={<div>Template ID</div>}>
                <Input name='templateId' value={state?.template?.templateId} disabled={true} />
              </FormField>
              <FormField label={<div>Owner</div>}>
                <Input name='ownerId' value={state?.template?.ownerId} disabled={true} />
              </FormField>
              <FormField label={<div>Name</div>} description='Name of the template'>
                <Input
                  name='templateName'
                  placeholder='Name of the template'
                  ariaRequired={true}
                  onChange={(event) => setTemplateName(event.detail.value)}
                  invalid={templateName !== null && validate.isFalsy(templateName)}
                  value={templateName}
                />
              </FormField>
              <FormField label={<div>Description</div>} description='A brief description about template.'>
                <Textarea
                  name='description'
                  placeholder='A brief description about the template'
                  ariaRequired={true}
                  onChange={(event) => setDescription(event.detail.value)}
                  invalid={description !== null && validate.isFalsy(description)}
                  value={description}
                />
              </FormField>
              <FormField label={<div>Template type</div>}>
                <Input name='ownerId' value={state?.template?.templateType} disabled={true} />
              </FormField>
              {isAccessManagementType(state?.template) &&
                getAccessManagementComponent(
                  simTemplateName,
                  setSimTemplateName,
                  simTemplateLink,
                  setSimTemplateLink,
                  approvalTemplateTitle,
                  setApprovalTemplateTitle,
                  approvalTemplateLink,
                  setApprovalTemplateLink,
                  autoApproval,
                  setAutoApproval,
                  allowMergeApprovalWorkflow,
                  setAllowMergeApprovalWorkflow,
                )}

              {isAccessRequestWorkflowType(state?.template) &&
                getAccessRequestWorkflowComponent(
                  arwRuleName,
                  setArwRuleName,
                  arwRuleDesc,
                  setArwRuleDesc,
                  arwRule,
                  setArwRule,
                  arwDataAgreementOptions,
                  arwDataAgreement,
                  setArwDataAgreement,
                  arwDataAgreementModalVisible,
                  setArwDataAgreementModalVisible,
                  setArwDataAgreementOwnerId,
                  arwDataAgreementId,
                  setArwDataAgreementId,
                  arwDataAgreementName,
                  setArwDataAgreementName,
                  approvalTemplateTitle,
                  setApprovalTemplateTitle,
                  approvalTemplateLink,
                  setApprovalTemplateLink,
                  autoApproval,
                  setAutoApproval,
                  allowMergeApprovalWorkflow,
                  setAllowMergeApprovalWorkflow,
                  priorityOrder,
                  setPriorityOrder,
                  optimisticApprovalEnabled,
                  setOptimisticApprovalEnabled,
                )}
            </SpaceBetween>
          </ColumnLayout>
        </Container>
      </Form>
    </div>
  );
};
